import { useCallback, useRef, useState } from "react";

import {
  SelectSheet,
  SelectSheetOption,
} from "../../../../components/MobileForm";
import { useResponsive } from "../../../../hooks/useResponsive";
import { BREAKPOINTS } from "../../../../utils/constants";
import DropdownMenu from "./DropdownMenu";
import { Chevron, DropdownWrapper, MenuItem, Pill, PillValue } from "./styles";
import { FilterDropdownProps } from "./types";
import { useDropdownDismiss } from "../../../../hooks/useDropdownDismiss";

const FilterDropdown: React.FC<FilterDropdownProps> = ({
  label,
  value,
  options,
  isActive,
  onChange,
  testId,
  align = "left",
}) => {
  const isCompact = useResponsive(BREAKPOINTS.tabletLarge);
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const close = useCallback(() => {
    setIsOpen(false);
  }, []);

  const toggle = useCallback(() => {
    setIsOpen((prev) => !prev);
  }, []);

  useDropdownDismiss(wrapperRef, menuRef, isOpen && !isCompact, close);

  const handleSelect = useCallback(
    (option: SelectSheetOption) => {
      onChange(option);
      setIsOpen(false);
    },
    [onChange],
  );

  const sheetTitle = label ? `Select ${label.toLowerCase()}` : "Select";

  return (
    <DropdownWrapper ref={wrapperRef}>
      <Pill
        type="button"
        $active={isActive}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label={label ? `${label}: ${value.label}` : value.label}
        onClick={toggle}
        data-testid={testId}
      >
        {label ? `${label}:` : null}
        <PillValue $active={isActive}>{value.label}</PillValue>
        <Chevron $isOpen={isOpen} />
      </Pill>

      {isCompact ? (
        <SelectSheet
          isOpen={isOpen}
          title={sheetTitle}
          options={options}
          selectedValue={value.value}
          onSelect={handleSelect}
          onClose={close}
        />
      ) : (
        isOpen && (
          <DropdownMenu anchorRef={wrapperRef} menuRef={menuRef} align={align}>
            {options.map((option) => {
              const isSelected = option.value === value.value;
              return (
                <MenuItem
                  key={option.value}
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  $selected={isSelected}
                  onClick={() => handleSelect(option)}
                >
                  {option.label}
                </MenuItem>
              );
            })}
          </DropdownMenu>
        )
      )}
    </DropdownWrapper>
  );
};

export default FilterDropdown;
